import type {
  ContentResponse,
  ContentUpdateRequest,
  ContentUpdateResponse,
  SectionsConfig,
  PagesListResponse,
  PageData,
  PageUpdateRequest,
  PageUpdateResponse,
  MediaListResponse,
  PresignRequest,
  PresignResponse,
  MediaDeleteResponse,
} from '@/types'

const API_BASE = '/api'

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Unknown error' })) as { error?: string }
    throw new ApiError(response.status, data.error || 'Request failed')
  }
  return response.json()
}

// Content API
export async function getContent(key: string, options?: { draft?: boolean }): Promise<ContentResponse> {
  const url = options?.draft
    ? `${API_BASE}/content/${key}?draft=true`
    : `${API_BASE}/content/${key}`
  const response = await fetch(url)
  return handleResponse<ContentResponse>(response)
}

export async function updateContent(
  key: string,
  data: ContentUpdateRequest
): Promise<ContentUpdateResponse> {
  const response = await fetch(`${API_BASE}/content/${key}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleResponse<ContentUpdateResponse>(response)
}

export async function getSectionsConfig(): Promise<SectionsConfig> {
  const response = await fetch(`${API_BASE}/content/config:sections`)
  const data = await handleResponse<ContentResponse>(response)
  return data.content as unknown as SectionsConfig
}

// Pages API
export async function getPages(): Promise<PagesListResponse> {
  const response = await fetch(`${API_BASE}/pages`)
  return handleResponse<PagesListResponse>(response)
}

export async function getPage(slug: string): Promise<PageData> {
  const response = await fetch(`${API_BASE}/pages/${slug}`)
  return handleResponse<PageData>(response)
}

export async function updatePage(
  slug: string,
  data: PageUpdateRequest
): Promise<PageUpdateResponse> {
  const response = await fetch(`${API_BASE}/pages/${slug}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleResponse<PageUpdateResponse>(response)
}

export async function deletePage(slug: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/pages/${slug}`, {
    method: 'DELETE',
  })
  return handleResponse<{ success: boolean }>(response)
}

// Media API
export async function getMedia(cursor?: string): Promise<MediaListResponse> {
  const url = cursor
    ? `${API_BASE}/media?cursor=${encodeURIComponent(cursor)}`
    : `${API_BASE}/media`
  const response = await fetch(url)
  return handleResponse<MediaListResponse>(response)
}

export async function getPresignedUrl(data: PresignRequest): Promise<PresignResponse> {
  const response = await fetch(`${API_BASE}/upload/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleResponse<PresignResponse>(response)
}

export async function uploadToR2(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percent = Math.round((e.loaded / e.total) * 100)
        onProgress(percent)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Upload failed')))
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.send(file)
  })
}

export async function deleteMedia(key: string): Promise<MediaDeleteResponse> {
  const response = await fetch(`${API_BASE}/media/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  })
  return handleResponse<MediaDeleteResponse>(response)
}

export { ApiError }
